import React from "react";
import Card from "components/Card/Card";
import { useForm, SubmitHandler } from "react-hook-form";
import Page from "components/Page/Page";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useActiveOrg } from "context/ActiveOrgContext";
import { createOrgMember } from "services/staff.service";
import vgToast from "utils/toast";

type FormValues = {
  firstName: string;
  lastName: string;
  emailAddress: string;
};

const NewStaff = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0) {
      vgToast.error("Select an active clinic before adding staff.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { data: res, error } = await createOrgMember(token, {
        orgId,
        contactFirstName: data.firstName,
        contactLastName: data.lastName,
        emailAddress: data.emailAddress,
      });
      if (error) {
        vgToast.error(error.message);
        return;
      }
      const newId =
        (res as { newRecordID?: number; NewRecordID?: number })?.newRecordID ??
        (res as { newRecordID?: number; NewRecordID?: number })?.NewRecordID;
      vgToast.success("Staff member created successfully.");
      if (newId != null && Number.isFinite(Number(newId))) {
        navigate(`/app/staff/${newId}`);
      } else {
        navigate("/app/staff");
      }
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card
        title="Add New Staff Member"
        subTitle="Fill the Following Details"
        className="mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-10">
          <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10">
            <Input
              {...register("firstName")}
              label="First Name"
              placeholder="Enter First Name"
            />

            <Input
              {...register("lastName")}
              label="Last Name"
              placeholder="Enter Last Name"
            />

            <Input
              {...register("emailAddress")}
              label="Email Address"
              placeholder="Enter Your Email"
            />
          </div>

          <Button color="Primary" type="submit" disabled={submitting}>
            <span>{submitting ? "Creating…" : "Create Staff Member"}</span>
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default NewStaff;
